import React, { useEffect, useState } from 'react';
import { useAccountStore } from '../../../../components/Stores/accountStore';
import { useFutureOrderStore } from '../../../../components/Stores/FutureOrdersStore';
import { useMaterialsStore } from '../../../../components/Stores/MaterialsStore';
import { IChartData } from '../../../../components/UI-Elements/Charts/DateValueInputMultiChart/DateValueInputMultiChart';
import { DateValueInputWithDateSwitch } from '../../../../components/UI-Elements/Charts/DateValueInputMultiChart/DateValueInputWithDateSwitch';
import { OptionSelect } from '../../../../components/UI-Elements/Select/OptionSelect';
import { createFutureOrder } from '../../../../sp-firebase/futureOrderFB';
import { IClientExposure, IFutureOrder } from '../../../../sp-types/FutureOrderType';
import { Product } from '../../../../sp-types/productType';
import { getHighestAbsoluteValue } from '../../../../utils/MonthYear/MonthYearValue';
import { FOModifyProductDeliveriesButtonSection } from '../FillOrderDetails/FillOrderDetailsButtonSection';
import { ClientExposureEditTable } from '../RawMaterialsExposure/GraphsTables/ClientExposureMaterialTable';
import {
  calculateMaterialsClientPayment,
  getClientCharts,
  getStartingDate,
  multiplyQuantityBy,
} from './FillOrderAdjustmentsUtils';
import { updateEditedFutureOrderCharts } from '../CreateOrderSummary/OrderSummaryUtils';
import { Timestamp } from 'firebase/firestore';
import { Material } from '../../../../sp-types';
import { getLastDate } from '../AddNewFutureOrderUtils';
import { useCurrencyRates } from '@hooks/useCurrencyRates';

const defaultAdjustment = 'Trailing month';

interface OrderAdjustmentForm {
  indexAdjustment: string;
  indexOffset: number;
  materialPayment: Record<string, IClientExposure>;
}

interface OrderIndexAdjustProps {
  products: Product[];
  materialsMap: Record<string, Material>;
  nextAction: () => void;
  backAction: () => void;
  isFutureOrderGotUpdated: boolean;
}
export const FOIndexedClientExposure: React.FC<OrderIndexAdjustProps> = ({
  materialsMap,
  products,
  backAction,
  nextAction,
  isFutureOrderGotUpdated,
}) => {
  const [isButtonsLoading, setIsButtonsLoading] = useState(false);
  const { editedFutureOrder, setEditedFutureOrder } = useFutureOrderStore();
  const { productId, orderDetails } = editedFutureOrder;
  const { account, formulaColorDictionary: colorsMap } = useAccountStore();
  const { AdjustmentsInfo } = account;
  const { materials } = useMaterialsStore();
  const { convertionMap, pricesMap } = useCurrencyRates();

  const [adjustmentDetails, setAdjustmentDetails] = useState(0);
  const firstMaterial = Object.keys(editedFutureOrder.clientExposure)[0];
  const [formValues, setFormValues] = React.useState<OrderAdjustmentForm>({
    indexAdjustment: editedFutureOrder.clientExposure[firstMaterial]?.adjustment || defaultAdjustment,
    indexOffset: editedFutureOrder.clientExposure[firstMaterial]?.indexOffset || 0,
    materialPayment: {},
  });

  useEffect(() => {
    if (formValues) {
      const { indexAdjustment, indexOffset, materialPayment } = formValues;
      const clientExposure = calculateMaterialsClientPayment(
        editedFutureOrder,
        editedFutureOrder.orderDetails.productDeliveryCalendar || [],
        materialPayment,
        indexAdjustment,
        indexOffset,
        materialsMap,
        getProduct(products, productId),
      );

      const updatedFuture = updateEditedFutureOrderCharts(
        { ...editedFutureOrder, clientExposure },
        products,
        materialsMap,
      );

      setEditedFutureOrder(updatedFuture);
    }
  }, [formValues, isFutureOrderGotUpdated]);

  const materialIndexChanged = (priceIndex: string, material: string) => {
    setFormValues((form) => ({
      ...form,

      materialPayment: {
        ...form.materialPayment,
        [material]: { ...form.materialPayment[material], priceIndex },
      },
    }));
  };

  const getProduct = (products: Product[], productId: string): Product | undefined =>
    products.find(({ uuid }) => uuid === productId);

  const calcMaterials = (order: IFutureOrder): Record<string, IClientExposure> =>
    calculateMaterialsClientPayment(
      order,
      order.orderDetails.productDeliveryCalendar || [],
      {},
      'Spot',
      0,
      materialsMap,
      getProduct(products, productId),
    );

  const getMaterialsCharts = (order: IFutureOrder, showAdjusted: boolean): IChartData[] =>
    getClientCharts(showAdjusted ? order.clientExposure : calcMaterials(order), materialsMap, colorsMap, convertionMap);

  return (
    <div className="flex flex-1 flex-col p-2 h-full overflow-y-auto overflow-x-hidden">
      <div className="flex flex-col w-full gap-20  border-b-[1px] border-b-gray-150">
        {adjustmentDetails === 0 ? ( //Index Picker
          <div className="flex flex-col grow  mt-[1rem] justify-between overflow-auto max-h-[15rem]">
            <ClientExposureEditTable
              editedFutureOrder={editedFutureOrder}
              materialsMap={materialsMap}
              showAdjustments={false}
              materialIndexChanged={materialIndexChanged}
              pricesMap={pricesMap}
            />
          </div>
        ) : (
          <div className="flex flex-col m-4">
            <div className="flex flex-row">
              <div className=" flex flex-row flex-1 max-w-72">
                <OptionSelect
                  showNoneOption={false}
                  currentOption={formValues.indexAdjustment}
                  options={AdjustmentsInfo.productAdjustmentOptions}
                  setCurrentOption={(indexAdjustment: string) =>
                    indexAdjustment && setFormValues((values) => ({ ...values, indexAdjustment }))
                  }
                  title="Index adjustment"
                />
              </div>
              {/* <div className="flex flex-col w-48 gap-1">
                <p className="text-lg">Index offset</p>
                <ToggleNumberInput
                  valueState={formValues.indexOffset}
                  setValueState={(indexOffset) => setFormValues((values) => ({ ...values, indexOffset }))}
                  downLimit={0}
                  upperLimit={11}
                  suffix="m"
                />
              </div> */}
            </div>
          </div>
        )}
      </div>

      <div className="flex flex-col flex-1"></div>
      <div>
        {orderDetails.productDeliveryCalendar && (
          <DateValueInputWithDateSwitch
            mainChart={{
              data: multiplyQuantityBy(orderDetails.productDeliveryCalendar || [], -(orderDetails.pricePerUnit || 1)),
              title: 'Product price',
              type: 'bar',
              transparent: true,
            }}
            isHideValueInput
            numberOfMonthsToShow={24}
            changeMonths={12}
            additionalCharts={getMaterialsCharts(editedFutureOrder, adjustmentDetails > 0)?.map((chart) => ({
              ...chart,
              data: chart.data.map((item) => ({
                ...item,
                value: item.value * (materials.find((item) => item.name === chart.title)?.currentPrice || 1),
              })),
            }))}
            startingDate={getStartingDate()}
            endDate={orderDetails.productDeliveryCalendar && getLastDate(orderDetails.productDeliveryCalendar)}
            suggestedMaxValue={
              orderDetails.productDeliveryCalendar &&
              getHighestAbsoluteValue(orderDetails.productDeliveryCalendar) * 1.2
            }
            suggestedMinValue={
              orderDetails.productDeliveryCalendar &&
              -getHighestAbsoluteValue(orderDetails.productDeliveryCalendar) * 1.2
            }
          />
        )}
      </div>
      <FOModifyProductDeliveriesButtonSection
        isNextLoading={isButtonsLoading}
        backAction={() => (adjustmentDetails === 0 ? backAction() : setAdjustmentDetails(0))}
        nextAction={() =>
          adjustmentDetails === 0
            ? setAdjustmentDetails(1)
            : editedFutureOrder.uuid &&
              account.id &&
              SaveOrderDetailsAndRoute(
                setIsButtonsLoading,
                editedFutureOrder.uuid,
                editedFutureOrder.clientExposure,
                account.id,
                nextAction,
              )
        }
      />
    </div>
  );
};

const SaveOrderDetailsAndRoute = async (
  setIsLoading: (_value: boolean) => void,
  futureOrderUuid: string,
  clientExposure: Record<string, IClientExposure>,
  accountId: string,
  routeFunc: (_newId?: string) => void,
) => {
  setIsLoading(true);
  await createFutureOrder(accountId, {
    status: 'underwriting',
    updatedAt: Timestamp.now(),
    uuid: futureOrderUuid,
    clientExposure: clientExposure,
  });
  setIsLoading(false);
  routeFunc();
};
